import React, { useMemo, useState } from 'react';
import { Paper, Stack, Group, MultiSelect, Button, Title, Text, Box, Grid, Badge, SegmentedControl, Table, ScrollArea, Alert } from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend } from 'recharts';
import { IconChartLine } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { transactionService } from '../../services/transactionService';
import { summarizeAnalytics, analyticsByDate } from '../../utils/transactionAnalytics';

const money = value => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value);

function PerformanceChart({ daily, mode, metric, t, height = 330 }) {
  const chartKey = metric === 'net' ? 'net' : 'spent';
  const projectedKey = metric === 'net' ? 'projectedNet' : 'projectedSpent';
  return (
    <Box h={height}><ResponsiveContainer width="100%" height="100%"><AreaChart data={daily} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0"/>
          <XAxis dataKey="date" tickFormatter={value => `${value.slice(8, 10)}.${value.slice(5, 7)}`} tick={{ fontSize: 12 }} minTickGap={24}/>
          <YAxis tick={{ fontSize: 11 }} tickFormatter={value => `$${Math.abs(value) >= 1000 ? `${(value / 1000).toFixed(1)}k` : value}`}/>
          <Tooltip formatter={value => money(value)} labelFormatter={value => `${value.slice(8, 10)}.${value.slice(5, 7)}.${value.slice(0, 4)}`} contentStyle={{ borderRadius: 12 }}/>
          <Legend/><ReferenceLine y={0} stroke="#94a3b8"/>
          {mode !== 'projected' && <Area type="linear" dataKey={chartKey} name={t.analytics_settled} stroke="#4f46e5" fill="#4f46e5" fillOpacity={0.08} strokeWidth={2.5} activeDot={{ r: 5 }}/ >}
          {mode !== 'settled' && <Area type="linear" dataKey={projectedKey} name={t.analytics_with_pending} stroke="#d97706" fill="#d97706" fillOpacity={0.04} strokeWidth={2.5} strokeDasharray="6 4"/>}
        </AreaChart></ResponsiveContainer></Box>
  );
}

export default function TransactionsDynamics({ users, t }) {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [data, setData] = useState([]);
  const [loadedFilters, setLoadedFilters] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('compare');
  const [metric, setMetric] = useState('net');
  const filters = JSON.stringify({ selectedUsers, selectedMonths });
  const stale = loadedFilters !== null && loadedFilters !== filters;
  const totals = useMemo(() => summarizeAnalytics(data), [data]);
  const daily = useMemo(() => analyticsByDate(data), [data]);
  const people = useMemo(() => {
    const grouped = new Map();
    data.forEach(row => {
      const key = row.userId ?? row.user?.email ?? 'Unknown';
      if (!grouped.has(key)) grouped.set(key, { name: row.user?.email || String(key), rows: [] });
      grouped.get(key).rows.push(row);
    });
    return [...grouped.values()].map(user => ({ ...user, daily: analyticsByDate(user.rows), ...summarizeAnalytics(user.rows) }))
      .sort((a, b) => (mode === 'projected' ? b.projectedNet - a.projectedNet : b.net - a.net));
  }, [data, mode]);
  const projected = mode === 'projected';
  const fetchData = async () => {
    if (!selectedUsers.length || !selectedMonths.length) {
      notifications.show({ title: t.filters_required, message: t.filters_msg, color: 'blue' });
      return;
    }
    setLoading(true);
    try {
      const months = selectedMonths.map(value => {
        const date = new Date(value);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      });
      const res = await transactionService.getStats(selectedUsers, months);
      if (!res.success) throw new Error('Analytics request failed');
      setData(res.transactions);
      setLoadedFilters(filters);
    } catch (error) {
      notifications.show({ title: t.error, message: t.error_analytics, color: 'red' });
    } finally { setLoading(false); }
  };
  const cards = [
    { label: t.revenue, value: totals.revenue, color: 'indigo', hint: t.analytics_revenue_hint },
    { label: t.total_spent, value: projected ? totals.projectedSpent : totals.spent, color: 'dark', hint: projected ? t.analytics_with_pending : t.analytics_settled },
    { label: t.net_profit, value: projected ? totals.projectedNet : totals.net, color: (projected ? totals.projectedNet : totals.net) >= 0 ? 'teal' : 'red', hint: mode === 'compare' ? `${t.analytics_with_pending}: ${money(totals.projectedNet)}` : t.analytics_business_net },
  ];
  return <Stack gap="lg">
    <Paper withBorder p="lg" radius="lg">
      <Group justify="space-between" mb="md"><Box><Title order={3}>{t.performance_overview}</Title><Text size="sm" c="dimmed">{t.analytics_intro}</Text></Box><Badge variant="light" size="lg">{t.analytics_business_net}</Badge></Group>
      <Grid align="flex-end">
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Group justify="space-between"><Text size="sm" fw={500}>{t.select_users}</Text><Button variant="subtle" size="compact-xs" disabled={loading} onClick={() => setSelectedUsers(users.map(u => String(u.id)))}>{t.select_all}</Button></Group>
          <MultiSelect aria-label={t.select_users} placeholder={t.pick_users} data={users.map(u => ({ value: String(u.id), label: u.email }))} value={selectedUsers} onChange={setSelectedUsers} searchable clearable disabled={loading} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}><MonthPickerInput type="multiple" label={t.select_months} placeholder={t.pick_months} value={selectedMonths} onChange={setSelectedMonths} clearable disabled={loading} /></Grid.Col>
        <Grid.Col span={{ base: 12, md: 3 }}><Button fullWidth leftSection={<IconChartLine size={16}/>} onClick={fetchData} loading={loading}>{t.generate_trends}</Button></Grid.Col>
      </Grid>
    </Paper>
    {stale && <Alert color="yellow">{t.analytics_stale}</Alert>}
    {data.length > 0 && <>
      <Group justify="space-between"><Text fw={600}>{t.analytics_cost_mode}</Text><SegmentedControl aria-label={t.analytics_cost_mode} value={mode} onChange={setMode} data={[{ value: 'settled', label: t.analytics_settled }, { value: 'projected', label: t.analytics_with_pending }, { value: 'compare', label: t.analytics_compare }]} /></Group>
      <Grid>
        {cards.map(card => <Grid.Col key={card.label} span={{ base: 12, sm: 6, lg: 3 }}><Paper withBorder radius="lg" p="lg" h="100%"><Text size="sm" c="dimmed">{card.label}</Text><Text size="26px" fw={750} c={card.color} my="xs">{money(card.value)}</Text><Text size="xs" c="dimmed">{card.hint}</Text></Paper></Grid.Col>)}
        <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}><Paper withBorder radius="lg" p="lg" h="100%"><Text size="sm" c="dimmed">ROI</Text><Text size="26px" fw={750} my="xs">{(projected ? totals.projectedRoi : totals.roi).toFixed(1)}%</Text><Text size="xs" c="dimmed">{mode === 'compare' ? `${t.analytics_with_pending}: ${totals.projectedRoi.toFixed(1)}%` : t.analytics_roi_hint}</Text></Paper></Grid.Col>
      </Grid>
      <Paper radius="lg" p="md" withBorder bg="indigo.0"><Group justify="space-between"><Box><Text fw={600}>{t.analytics_pending}: {money(totals.pending)}</Text><Text size="sm" c="dimmed">{t.analytics_pending_hint}</Text></Box><Text size="sm">{t.analytics_pending_fees}: {money(totals.pendingFees)}</Text></Group></Paper>
      <Paper withBorder radius="lg" p="lg">
        <Group justify="space-between" mb="lg"><Box><Title order={4}>{t.analytics_daily}</Title><Text size="sm" c="dimmed">{t.analytics_chart_hint}</Text></Box><SegmentedControl aria-label={t.analytics_metric} value={metric} onChange={setMetric} data={[{ value: 'net', label: t.net_profit }, { value: 'spent', label: t.total_spent }]} /></Group>
        <PerformanceChart daily={daily} mode={mode} metric={metric} t={t} />
      </Paper>
      <Box>
        <Title order={4} mb="xs">{t.individual_trends}</Title>
        <Text size="sm" c="dimmed" mb="md">{t.analytics_chart_hint}</Text>
        <Grid>
          {people.map(user => (
            <Grid.Col key={user.name} span={{ base: 12, lg: 6 }}>
              <Paper withBorder radius="lg" p="lg" h="100%">
                <Group justify="space-between" mb="md" align="flex-start">
                  <Box>
                    <Text fw={700}>{user.name}</Text>
                    <Text size="xs" c="dimmed">{t.analytics_business_net}</Text>
                  </Box>
                  <Box ta="right">
                    <Text size="xs" c="dimmed">{t.net_profit}</Text>
                    <Text fw={700} c={(projected ? user.projectedNet : user.net) >= 0 ? 'teal' : 'red'}>
                      {money(projected ? user.projectedNet : user.net)}
                    </Text>
                    {mode === 'compare' && (
                      <Text size="xs" c="orange.8">
                        {t.analytics_with_pending}: {money(user.projectedNet)}
                      </Text>
                    )}
                  </Box>
                </Group>
                <PerformanceChart daily={user.daily} mode={mode} metric={metric} t={t} height={230} />
                <Group justify="space-between" mt="md" gap="xs">
                  <Text size="xs" c="dimmed">ROI: {(projected ? user.projectedRoi : user.roi).toFixed(1)}%</Text>
                  <Text size="xs" c="dimmed">{t.days}: {user.daily.length}</Text>
                  <Text size="xs" c="dimmed">{t.total_spent}: {money(projected ? user.projectedSpent : user.spent)}</Text>
                </Group>
              </Paper>
            </Grid.Col>
          ))}
        </Grid>
      </Box>
      <Paper withBorder radius="lg" p="lg"><Title order={4} mb="xs">{t.analytics_users}</Title><Text size="sm" c="dimmed" mb="md">{t.analytics_users_hint}</Text><ScrollArea><Table miw={760} verticalSpacing="sm" highlightOnHover><Table.Thead><Table.Tr>
        {[t.user, t.revenue, t.total_spent, t.analytics_pending, t.net_profit, ...(mode === 'compare' ? [t.analytics_projected_net] : []), 'ROI'].map(label => <Table.Th key={label}>{label}</Table.Th>)}
      </Table.Tr></Table.Thead><Table.Tbody>{people.map(user => <Table.Tr key={user.name}>
        <Table.Td><Text fw={600} size="sm">{user.name}</Text></Table.Td><Table.Td>{money(user.revenue)}</Table.Td><Table.Td>{money(projected ? user.projectedSpent : user.spent)}</Table.Td><Table.Td><Text c={user.pending ? 'orange.8' : 'dimmed'} size="sm">{money(user.pending)}</Text></Table.Td><Table.Td><Text fw={600} size="sm" c={(projected ? user.projectedNet : user.net) >= 0 ? 'teal' : 'red'}>{money(projected ? user.projectedNet : user.net)}</Text></Table.Td>{mode === 'compare' && <Table.Td>{money(user.projectedNet)}</Table.Td>}<Table.Td>{(projected ? user.projectedRoi : user.roi).toFixed(1)}%</Table.Td>
      </Table.Tr>)}</Table.Tbody></Table></ScrollArea></Paper>
    </>}
    {!data.length && !loading && <Paper withBorder p="xl" radius="lg" ta="center"><IconChartLine size={36} color="#818cf8"/><Text c="dimmed" mt="sm">{loadedFilters !== null ? t.analytics_empty : t.analytics_placeholder}</Text></Paper>}
  </Stack>;
}
